import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Drawer from '~/components/Drawer';
import {
    inputStyles,
    labelStyles,
    requiredStyles,
    sectionStyles,
} from './styles';
import { FaPlus } from 'react-icons/fa6';
import { RiDeleteBinLine } from 'react-icons/ri';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { updateMedicalProfessional } from '~/services/medical-professionals';
import { apiUrl, authHeaders } from '~/services/auth';

interface SelectOption {
    value: number | string;
    label: string;
}

const US_STATES: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

const stateOptions: SelectOption[] = Object.entries(US_STATES).map(
    ([value, label]) => ({ value, label }),
);

// Normalize an arbitrary phone string into (XXX) XXX-XXXX so existing data
// loaded from the API matches the form's validation pattern. Used both when
// populating the form and as the user types.
const formatPhoneNumber = (value: string): string => {
    let digits = (value ?? '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    let formatted = '';
    if (digits.length >= 1) formatted = `(${digits.slice(0, 3)}`;
    if (digits.length >= 4) formatted += `) ${digits.slice(3, 6)}`;
    if (digits.length >= 7) formatted += `-${digits.slice(6, 10)}`;
    return formatted;
};

const searchMedicalCenters = async (state: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalCenters`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ state }),
    });
    if (!resp.ok) throw new Error('Failed to search medical centers');
    return await resp.json();
};

interface Props {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    medProf?: any;
    professionalChoices: any;
    setMedicalProf: any;
}

type PhoneEntry = { phone: string; type: string };

type EditMedicalProfessional = {
    person: {
        first_name: string;
        middle_name: string;
        last_name: string;
        address_1: string;
        address_2: string;
        city: string;
        state: string;
        zip_code: string;
        email: string;
        phone_set: PhoneEntry[];
    };
    update_photo: boolean;
    medical_professional_type: any;
    specialty: any;
    type: SelectOption[];
    medical_center: any;
    state?: string;
    npi_num?: string;
};

const defaultEditMedicalProfessional: EditMedicalProfessional = {
    person: {
        first_name: '',
        middle_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        zip_code: '',
        email: '',
        phone_set: [],
    },
    update_photo: false,
    medical_professional_type: '',
    specialty: '',
    type: [],
    medical_center: '',
    npi_num: '',
};

const MedicalProfessionalDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    medProf,
    professionalChoices,
    setMedicalProf,
}) => {
    const {
        register,
        reset,
        control,
        handleSubmit,
        formState: { isSubmitting },
    } = useForm<EditMedicalProfessional>({
        defaultValues: defaultEditMedicalProfessional,
    });

    const { fields, append, remove } = useFieldArray({
        name: 'person.phone_set',
        control,
    });

    const [selectedState, setSelectedState] = useState<SelectOption | null>(
        null,
    );
    const [selectedMedicalCenter, setSelectedMedicalCenter] =
        useState<SelectOption | null>(null);
    const [medicalCenterOptions, setMedicalCenterOptions] = useState<
        SelectOption[]
    >([]);
    const [loadingCenters, setLoadingCenters] = useState(false);

    const loadMedicalCenters = async (
        stateName: string,
        preselectedCenterId?: number,
    ) => {
        setLoadingCenters(true);
        try {
            const centers = await searchMedicalCenters(stateName);
            const opts: SelectOption[] = centers.map((center: any) => ({
                label: center.name,
                value: center.id,
            }));
            setMedicalCenterOptions(opts);
            if (preselectedCenterId) {
                const match = opts.find(
                    (opt) => opt.value === preselectedCenterId,
                );
                if (match) setSelectedMedicalCenter(match);
            }
        } catch (error) {
            console.error('Error loading medical centers:', error);
        } finally {
            setLoadingCenters(false);
        }
    };

    useEffect(() => {
        if (medProf && medProf.person) {
            const person = medProf.person;
            const obj: EditMedicalProfessional = {
                person: {
                    first_name: person?.first_name ?? '',
                    middle_name: person?.middle_name ?? '',
                    last_name: person?.last_name ?? '',
                    address_1: person?.address_1 ?? '',
                    address_2: person?.address_2 ?? '',
                    city: person?.city ?? '',
                    state: person?.state ?? '',
                    zip_code: person?.zip_code ?? '',
                    email: person?.email ?? '',
                    phone_set: (person?.phone_set ?? []).map(
                        (p: PhoneEntry) => ({
                            ...p,
                            phone: formatPhoneNumber(p.phone),
                        }),
                    ),
                },
                update_photo: false,
                medical_professional_type:
                    medProf?.medical_professional_type?.id ?? '',
                specialty: medProf?.medical_reason?.id ?? '',
                type:
                    medProf?.medical_issues?.map((issue: any) => ({
                        value: issue?.id,
                        label: issue?.name,
                    })) ?? [],
                medical_center: medProf?.medical_center?.id ?? '',
                npi_num: medProf?.npi_num ?? '',
            };
            reset(obj);

            // Initialize medical-center search using the medical center's state.
            // The address state above (person.state) is a separate control.
            const location = medProf?.medical_center?.location;
            if (location) {
                const stateOpt = stateOptions.find(
                    (opt) => opt.label === location,
                );
                if (stateOpt) {
                    setSelectedState(stateOpt);
                    loadMedicalCenters(location, medProf?.medical_center?.id);
                    return;
                }
            }
            setSelectedState(null);
            setSelectedMedicalCenter(null);
            setMedicalCenterOptions([]);
        }
    }, [medProf]);

    const handleStateChange = (option: SelectOption | null) => {
        setSelectedState(option);
        setSelectedMedicalCenter(null);
        setMedicalCenterOptions([]);
        if (option) {
            loadMedicalCenters(option.label);
        }
    };

    const handleMedicalCenterChange = (option: SelectOption | null) => {
        setSelectedMedicalCenter(option);
    };

    const onSubmit = async (data: EditMedicalProfessional) => {
        const payload: any = {
            ...data,
            type: (data.type ?? []).map((issue) => issue.value),
            medical_center: selectedMedicalCenter
                ? selectedMedicalCenter.value
                : '',
            // Backend reads the address state at the top level (data.state),
            // mirrors it onto person.state via Us_State lookup by abbreviation.
            state: data.person?.state ?? '',
        };
        const updatedMedProf = await updateMedicalProfessional(
            medProf.id,
            payload,
        );
        setMedicalProf(updatedMedProf);
        setIsOpen(false);
    };

    const medicalIssueOptions = professionalChoices?.medical_issues?.map(
        (issue: any) => {
            return {
                value: issue[0],
                label: issue[1],
            };
        },
    );
    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            title={'Edit Medical Professional'}
        >
            <h2 className={sectionStyles}>CONTACT</h2>

            <form onSubmit={handleSubmit(onSubmit)}>
                <label htmlFor='cid-first-name' className={labelStyles}>
                    First Name<span className={requiredStyles}>*</span>
                </label>
                <input
                    id='cid-first-name'
                    className={`${inputStyles} w-full`}
                    {...register('person.first_name')}
                />

                <label htmlFor='cid-middle-name' className={labelStyles}>
                    Middle Name
                </label>
                <input
                    id='cid-middle-name'
                    className={`${inputStyles} w-full`}
                    {...register('person.middle_name')}
                />

                <label htmlFor='cid-last-name' className={labelStyles}>
                    Last Name<span className={requiredStyles}>*</span>
                </label>
                <input
                    id='cid-last-name'
                    className={`${inputStyles} w-full`}
                    {...register('person.last_name')}
                />

                <label htmlFor='cid-email' className={labelStyles}>
                    Email<span className={requiredStyles}>*</span>
                </label>
                <input
                    id='cid-email'
                    className={`${inputStyles} w-full`}
                    {...register('person.email')}
                />

                {fields.map((field, index) => (
                    <div className='flex items-center gap-[8px]' key={field.id}>
                        <div className='w-1/2'>
                            <label className={`w-full ${labelStyles}`}>
                                Contact Number
                                <span className={requiredStyles}>*</span>
                            </label>
                            <Controller
                                name={`person.phone_set.${index}.phone`}
                                control={control}
                                rules={{
                                    required: true,
                                    pattern: {
                                        value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                        message:
                                            'Please enter a valid phone number in format (123) 456-7890',
                                    },
                                }}
                                render={({
                                    field: phoneField,
                                    fieldState,
                                }) => (
                                    <>
                                        <input
                                            className={`${inputStyles} w-full`}
                                            type='tel'
                                            placeholder='(123) 456-7890'
                                            maxLength={14}
                                            onChange={(e) =>
                                                phoneField.onChange(
                                                    formatPhoneNumber(
                                                        e.target.value,
                                                    ),
                                                )
                                            }
                                            value={phoneField.value}
                                        />
                                        {fieldState.error && (
                                            <p className='mt-[4px] text-[12px] text-red'>
                                                {fieldState.error.message ||
                                                    'This field is required'}
                                            </p>
                                        )}
                                    </>
                                )}
                            />
                        </div>

                        <div className='w-1/4'>
                            <label className={`w-full ${labelStyles}`}>
                                Type<span className={requiredStyles}>*</span>
                            </label>
                            <select
                                className={`${inputStyles} w-full`}
                                {...register(`person.phone_set.${index}.type`)}
                            >
                                <option value=''>Select</option>
                                <option value='Cell'>Cell</option>
                                <option value='Direct'>Direct</option>
                                <option value='Fax'>Fax</option>
                                <option value='Home'>Home</option>
                                <option value='Main'>Main</option>
                                <option value='Office'>Office</option>
                                <option value='Pager'>Pager</option>
                            </select>
                        </div>

                        <div className='self-end'>
                            <button
                                type='button'
                                aria-label='Remove phone number'
                                onClick={() => remove(index)}
                            >
                                <RiDeleteBinLine
                                    className='text-red'
                                    size={20}
                                />
                            </button>
                        </div>
                    </div>
                ))}

                <button
                    className='mt-[16px] flex items-center gap-[8px] text-[14px]/[18px] font-bold text-[#00AEB3]'
                    onClick={() => append({ phone: '', type: '' })}
                    type='button'
                >
                    <FaPlus />
                    ADD PHONE NUMBER
                </button>

                <h2 className={sectionStyles}>ADDRESS</h2>

                <label htmlFor='cid-address-1' className={labelStyles}>
                    Street Address 1<span className={requiredStyles}>*</span>
                </label>
                <input
                    id='cid-address-1'
                    className={`${inputStyles} w-full`}
                    {...register('person.address_1')}
                />

                <label htmlFor='cid-address-2' className={labelStyles}>
                    Street Address 2
                </label>
                <input
                    id='cid-address-2'
                    className={`${inputStyles} w-full`}
                    {...register('person.address_2')}
                />

                <label htmlFor='cid-city' className={labelStyles}>
                    City<span className={requiredStyles}>*</span>
                </label>
                <input
                    id='cid-city'
                    className={`${inputStyles} w-full`}
                    {...register('person.city')}
                />

                <label htmlFor='cid-state' className={labelStyles}>
                    State<span className={requiredStyles}>*</span>
                </label>
                <Controller
                    name='person.state'
                    control={control}
                    render={({ field }) => (
                        <Select
                            menuPlacement='auto'
                            inputId='cid-state'
                            options={stateOptions}
                            value={
                                stateOptions.find(
                                    (opt) => opt.value === field.value,
                                ) ?? null
                            }
                            onChange={(opt) =>
                                field.onChange(opt ? opt.value : '')
                            }
                            placeholder='Select...'
                            isClearable
                        />
                    )}
                />

                <label htmlFor='cid-zip-code' className={labelStyles}>
                    Zip Code<span className={requiredStyles}>*</span>
                </label>
                <input
                    id='cid-zip-code'
                    className={`${inputStyles} w-full`}
                    {...register('person.zip_code')}
                />

                <h2 className={sectionStyles}>PROFESSIONAL INFORMATION</h2>

                <label
                    htmlFor='cid-professional-type'
                    className={`w-full ${labelStyles}`}
                >
                    Professional Type<span className={requiredStyles}>*</span>
                </label>
                <select
                    id='cid-professional-type'
                    className={`${inputStyles} w-full`}
                    {...register(`medical_professional_type`)}
                >
                    <option value=''>Select...</option>
                    {professionalChoices?.medical_professional_types?.map(
                        (type: any) => (
                            <option key={type[0]} value={type[0]}>
                                {type[1]}
                            </option>
                        ),
                    )}
                </select>

                <label
                    htmlFor='cid-specialty'
                    className={`w-full ${labelStyles}`}
                >
                    Specialty
                </label>
                <select
                    id='cid-specialty'
                    className={`${inputStyles} w-full`}
                    {...register(`specialty`)}
                >
                    <option value=''>Select...</option>
                    {professionalChoices?.medical_professional_medical_reasons?.map(
                        (type: any) => (
                            <option key={type[0]} value={type[0]}>
                                {type[1]}
                            </option>
                        ),
                    )}
                </select>
                <label htmlFor='cid-type' className={`w-full ${labelStyles}`}>
                    Type
                </label>
                <Controller
                    name='type'
                    control={control}
                    render={({ field }) => (
                        <Select
                            menuPlacement='auto'
                            isMulti
                            className={`${inputStyles} w-full`}
                            value={field.value}
                            onChange={field.onChange}
                            options={medicalIssueOptions}
                        ></Select>
                    )}
                />

                <label
                    htmlFor='cid-npi-num'
                    className={`w-full ${labelStyles}`}
                >
                    NPI Number
                </label>
                <input
                    id='cid-npi-num'
                    className={`${inputStyles} w-full`}
                    {...register('npi_num')}
                    placeholder='NPI Number'
                />

                <label className={`w-full ${labelStyles}`}>
                    Medical Center State
                </label>
                <Select
                    menuPlacement='auto'
                    options={stateOptions}
                    value={selectedState}
                    onChange={handleStateChange}
                    placeholder='Select a state...'
                    className='mb-[8px]'
                />

                <label className={`w-full ${labelStyles}`}>
                    Medical Center
                </label>
                <Select
                    menuPlacement='auto'
                    options={medicalCenterOptions}
                    value={selectedMedicalCenter}
                    onChange={handleMedicalCenterChange}
                    placeholder='Select a medical center...'
                    isLoading={loadingCenters}
                    isDisabled={!selectedState || loadingCenters}
                    isClearable
                />
                <button
                    type='button'
                    className='hover: mt-[30px] w-full grow rounded-full border-[2px] border-danger bg-danger px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] hover:text-black'
                >
                    Deactivate
                </button>
                <div className='mb-[30px] flex gap-[16px]'>
                    <button
                        type='button'
                        className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={() => setIsOpen(false)}
                    >
                        Cancel
                    </button>
                    <button
                        type='submit'
                        disabled={isSubmitting}
                        className='mt-[30px] grow rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-warning disabled:hover:bg-warning'
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default MedicalProfessionalDrawer;
